/**
 * EditProfileSection — the /profile page: an always-editable profile form.
 * Opens in view mode (read-only details + profile completeness); Edit switches to the form.
 * Mirrors Flutter WebEditProfile:
 *   - user info card: avatar, "displayId | first last", current role, current location,
 *     and the Documents block (resume name, "CV last Updated", Upload Resume / Download)
 *   - dynamic form fields from the editProfile schema
 *   - Cancel (→ /viewJobs) / Save
 *   - edit profile note from tenant preferences + support email
 * Redesign layout: identity card and form card in the main column, a sticky Documents card
 * beside them (stacked on mobile).
 *
 * Flutter source:
 *   lib/presentation/web_profile/web_edit_profile/web_edit_profile.dart
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useEditProfile } from '../hooks/useEditProfile';
import { DynamicFormField } from '@/features/application/components/DynamicFormField';
import { Toast } from '@/features/home/components/Toast';
import { useLocalizationStore } from '@/store/localizationStore';
import { FILE_ACCEPT_STRING, FieldType } from '@/types/application.types';
import { buildProfileRows, computeCompleteness, type ProfileGroup, type ProfileRow } from '../utils/profileView';
import styles from './EditProfileSection.module.css';

export function EditProfileSection() {
  const { t } = useTranslation();
  const language = useLocalizationStore((s) => s.currentLanguage);
  const { state, fileInputRef, actions, config } = useEditProfile();
  const { fields } = state;
  // Opens in view mode; Edit switches to the form
  const [isEditing, setIsEditing] = useState(false);

  const candidate = state.candidateDetails;
  const candidateUrl = candidate?.candidateUrl ?? '';
  const hasResume = !!candidateUrl;
  const resumeUpdatedOn = formatDateFromEpoch(candidate?.resumeUpdateOn ?? 0, language);
  // Flutter: "${displayId ?? ""} | ${firstName ?? ""} ${lastName ?? ""}"
  const identity = `${candidate?.displayId ?? ''} | ${candidate?.firstName ?? ''} ${candidate?.lastName ?? ''}`;
  const fullName = `${candidate?.firstName ?? ''} ${candidate?.lastName ?? ''}`.trim();

  const rows = useMemo(() => buildProfileRows(fields), [fields]);
  const currentRole = candidate?.currentRole ?? '';
  const roleLabel = rows.find((r) => /role/i.test(r.key))?.label ?? t('Current Role');
  const locationLabel = rows.find((r) => /location/i.test(r.key))?.label ?? t('Current Location');
  const experienceLabel = rows.find((r) => /experience/i.test(r.label))?.label ?? t('Total Professional Experience');
  const experienceValue = rows.find((r) => r.group === 'professional' && /experience/i.test(r.label))?.value ?? '';
  const completeness = useMemo(
    () => computeCompleteness(rows, { title: t('Resume'), present: hasResume }),
    [rows, hasResume, t],
  );

  const handleCancel = () => {
    actions.reload();
    setIsEditing(false);
  };

  const monthsIndexFor = (idx: number) =>
    fields[idx]?.type === FieldType.EXPERIENCE_DROP_DOWN &&
    fields[idx + 1]?.key === 'experienceInMonths'
      ? idx + 1
      : -1;

  const renderField = (idx: number) => {
    const field = fields[idx];
    // Rendered together with the years field (Flutter skips the experienceInMonths entry)
    if (field.key === 'experienceInMonths') return null;
    const monthsIdx = monthsIndexFor(idx);
    return (
      <div key={field.key || idx} className={styles.fieldCell}>
        <DynamicFormField
          field={field}
          onChange={(val, displayValue) => actions.updateField(idx, val, displayValue)}
          error={state.errors[field.key]}
          validator={actions.validateField}
          disabled={state.isBusy}
          monthsField={monthsIdx >= 0 ? fields[monthsIdx] : undefined}
          onMonthsChange={monthsIdx >= 0 ? (val) => actions.updateField(monthsIdx, val) : undefined}
          onCountryCodeChange={
            field.isMobileFld ? (code) => actions.updateCountryCode(idx, code) : undefined
          }
          fetchSuggestions={
            field.key === 'currentLocation' ? actions.fetchLocationSuggestions : undefined
          }
        />
      </div>
    );
  };

  return (
    <div className={styles.page}>
      <input
        ref={fileInputRef}
        type="file"
        accept={FILE_ACCEPT_STRING}
        className={styles.hiddenInput}
        onChange={actions.handleFileSelect}
        aria-label={t('Upload Resume')}
      />

      {state.isLoading ? (
        // Flutter ModalProgressHUD over an empty page until the schema and details load
        <div className={styles.loadingContainer} role="status" aria-label={t('Loading...')}>
          <div className={styles.spinner} />
        </div>
      ) : (
        <div className={styles.container}>
          <div className={styles.layout}>
            <div className={styles.main}>
              {/* ── Profile hero (Flutter _buildUserInfoCard): cover, avatar, name, key facts ── */}
              <section className={styles.hero} aria-label={t('Profile')}>
                <div className={styles.heroCover} aria-hidden="true" />
                <div className={styles.heroBody}>
                  <div className={styles.avatar} aria-hidden="true">
                    <UserIcon className={styles.avatarIcon} />
                  </div>
                  <div className={styles.heroMain}>
                    <h1 className={styles.userName}>{fullName || identity}</h1>
                    {currentRole ? (
                      <p className={styles.headline}>{currentRole}</p>
                    ) : (
                      <p className={`${styles.headline} ${styles.headlineEmpty}`}>{`${roleLabel}: ${t('Not provided')}`}</p>
                    )}
                    <ul className={styles.metaList}>
                      {candidate?.displayId && (
                        <li className={styles.metaItem}>
                          <IdIcon className={styles.metaIcon} />
                          <span className={styles.metaText}>{`${t('Candidate ID')}: ${candidate.displayId}`}</span>
                        </li>
                      )}
                      {candidate?.currentLocation && (
                        <li className={styles.metaItem}>
                          <LocationIcon className={styles.metaIcon} />
                          <span className={styles.metaText}>{candidate.currentLocation}</span>
                        </li>
                      )}
                      {!candidate?.currentLocation && (
                        <li className={`${styles.metaItem} ${styles.metaEmpty}`}>
                          <LocationIcon className={styles.metaIcon} />
                          <span className={styles.metaText}>{`${locationLabel}: ${t('Not provided')}`}</span>
                        </li>
                      )}
                      <li className={`${styles.metaItem} ${experienceValue ? '' : styles.metaEmpty}`}>
                        <BriefcaseIcon className={styles.metaIcon} />
                        <span className={styles.metaText}>
                          {experienceValue || `${experienceLabel}: ${t('Not provided')}`}
                        </span>
                      </li>
                    </ul>
                  </div>
                  {!isEditing && (
                    <button type="button" className={styles.editBtn} onClick={() => setIsEditing(true)}>
                      <PencilIcon className={styles.btnIcon} />
                      {t('Edit Profile')}
                    </button>
                  )}
                </div>
              </section>

              {!isEditing ? (
                /* ── View mode: details grouped into sections ── */
                <>
                  {VIEW_GROUPS.map(({ group, title, icon }) => {
                    const groupRows = rows.filter((r) => r.group === group && !r.inHero);
                    if (groupRows.length === 0) return null;
                    return (
                      <section key={group} className={styles.card}>
                        <div className={styles.cardTitle}>
                          <span className={styles.cardTitleBar} aria-hidden="true" />
                          <h2 className={styles.cardTitleText}>{t(title)}</h2>
                        </div>
                        <dl className={styles.viewGrid}>
                          {groupRows.map((row) => (
                            <div key={row.key} className={styles.viewItem}>
                              <span className={styles.viewIcon} aria-hidden="true">{icon(row)}</span>
                              <div className={styles.viewText}>
                                <dt className={styles.viewLabel}>{row.label}</dt>
                                <dd className={`${styles.viewValue} ${row.value ? '' : styles.viewEmpty}`}>
                                  {row.value ? <ViewValue row={row} /> : t('Not provided')}
                                </dd>
                              </div>
                            </div>
                          ))}
                        </dl>
                      </section>
                    );
                  })}
                </>
              ) : (
              /* ── Form (Flutter _buildForm) + Cancel / Save ── */
              <form
                className={styles.editForm}
                noValidate
                onSubmit={(e) => {
                  e.preventDefault();
                  void actions.handleSave().then((saved) => {
                    if (saved) setIsEditing(false);
                  });
                }}
              >
                <div className={styles.card}>
                  <div className={styles.cardTitle}>
                    <span className={styles.cardTitleBar} aria-hidden="true" />
                    <h2 className={styles.cardTitleText}>{t('Edit Profile')}</h2>
                  </div>
                  <div className={styles.formGrid}>{fields.map((_, idx) => renderField(idx))}</div>
                </div>
                <div className={styles.actionBar}>
                  <button
                    type="button"
                    className={styles.cancelBtn}
                    onClick={handleCancel}
                    disabled={state.isBusy}
                  >
                    {t('Cancel')}
                  </button>
                  <button
                    type="submit"
                    className={styles.saveBtn}
                    disabled={!state.enableSave || state.isBusy}
                  >
                    {t('Save')}
                  </button>
                </div>
              </form>
              )}
            </div>

            {/* ── Documents (Flutter _buildDocumentsSection) ── */}
            <aside className={styles.aside}>
              <section className={styles.card}>
                <div className={styles.cardTitle}>
                  <span className={styles.cardTitleBar} aria-hidden="true" />
                  <h2 className={styles.cardTitleText}>{t('Documents')}</h2>
                </div>
                <div className={styles.documentsBody}>
                  <div className={styles.resumeTile}>
                    <span className={styles.resumeFileIcon} aria-hidden="true">
                      <FileIcon />
                    </span>
                    {hasResume ? (
                      <ResumeFileName url={candidateUrl} className={styles.resumeName} />
                    ) : (
                      <span className={styles.resumeSpacer} />
                    )}
                  </div>
                  <div className={styles.resumeUpdated}>
                    {/* Flutter shows this label untranslated */}
                    <span className={styles.resumeUpdatedLabel}>CV last Updated</span>
                    <span className={styles.resumeDate}>{resumeUpdatedOn}</span>
                  </div>
                  <div className={styles.documentsActions}>
                    <button
                      type="button"
                      className={styles.primaryBtn}
                      onClick={actions.triggerFileSelect}
                      disabled={state.isBusy}
                    >
                      <UploadIcon className={styles.btnIcon} />
                      {t('Upload Resume')}
                    </button>
                    <button
                      type="button"
                      className={styles.outlineBtn}
                      onClick={actions.handleDownloadResume}
                      disabled={!hasResume || state.isBusy}
                    >
                      <DownloadIcon className={styles.btnIcon} />
                      {t('Download')}
                    </button>
                  </div>
                </div>
              </section>

              {/* ── Profile completeness ── */}
              <section className={styles.card} aria-label={t('Profile Strength')}>
                <div className={styles.cardTitle}>
                  <span className={styles.cardTitleBar} aria-hidden="true" />
                  <h2 className={styles.cardTitleText}>{t('Profile Strength')}</h2>
                </div>
                <div className={styles.completenessBody}>
                  <div
                    className={styles.ring}
                    role="progressbar"
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={completeness.percent}
                    style={{ ['--pct' as string]: completeness.percent }}
                  >
                    <span className={styles.ringValue}>{completeness.percent}%</span>
                  </div>
                  <p className={styles.completenessLead}>
                    {completeness.missing.length === 0
                      ? t('Your profile is complete')
                      : t('Complete your profile to stand out to recruiters')}
                  </p>
                </div>
                {completeness.missing.length > 0 && (
                  <ul className={styles.todoList}>
                    {completeness.missing.map((title) => (
                      <li key={title}>
                        {isEditing ? (
                          <span className={styles.todoItem}>{title}</span>
                        ) : (
                          <button type="button" className={styles.todoItem} onClick={() => setIsEditing(true)}>
                            <PlusIcon className={styles.todoIcon} />
                            {title}
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </aside>
          </div>

          {config.editProfileNote && (
            <p className={styles.editProfileNote}>
              {t(config.editProfileNote)}{' '}
              <a href={`mailto:${config.supportEmailId}`} className={styles.noteLink}>
                {config.supportEmailId}
              </a>
            </p>
          )}
        </div>
      )}

      {state.isBusy && (
        <div className={styles.busyOverlay} role="status" aria-label={t('Loading...')}>
          <div className={styles.spinner} />
        </div>
      )}

      {state.errorMessage && (
        <Toast message={state.errorMessage} type="error" onClose={actions.clearError} />
      )}
      {state.successMessage && (
        <Toast message={state.successMessage} type="success" onClose={actions.clearSuccess} />
      )}
    </div>
  );
}

const VIEW_GROUPS: { group: ProfileGroup; title: string; icon: (row: ProfileRow) => ReactNode }[] = [
  { group: 'personal', title: 'Personal Details', icon: () => <UserSmallIcon /> },
  { group: 'professional', title: 'Professional Details', icon: (row) => (/location/i.test(row.label) ? <LocationIcon /> : <BriefcaseIcon />) },
  {
    group: 'contact',
    title: 'Contact Information',
    icon: (row) => (/linkedin/i.test(row.label) ? <LinkIcon /> : /mail/i.test(row.label) ? <MailIcon /> : <PhoneIcon />),
  },
];

/** A row's value; LinkedIn and email values are links */
function ViewValue({ row }: { row: ProfileRow }) {
  if (/linkedin/i.test(row.label)) {
    const href = /^https?:\/\//i.test(row.value) ? row.value : `https://${row.value}`;
    return <a href={href} target="_blank" rel="noopener noreferrer" className={styles.viewLink}>{row.value}</a>;
  }
  if (/mail/i.test(row.label)) return <a href={`mailto:${row.value}`} className={styles.viewLink}>{row.value}</a>;
  return <>{row.value}</>;
}

/**
 * Resume name sized to the space it gets — Flutter LayoutBuilder:
 * maxLength = clamp(maxWidth / 8, 23, 53) - 8, then GenericHelpers.extractFileName.
 */
function ResumeFileName({ url, className }: { url: string; className: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const maxLength = Math.min(53, Math.max(23, Math.floor(width / 8))) - 8;
  return (
    <span ref={ref} className={className}>
      {extractFileName(url, maxLength)}
    </span>
  );
}

/* ── Helpers ── */

/**
 * Mirrors Flutter GenericHelpers.extractFileName: drop the leading timestamp (`^\d+_?`), and
 * past maxLength keep max(maxLength, minLength) characters + ".." + the extension.
 */
function extractFileName(input: string, maxLength = 50, minLength = 20): string {
  const fileName = input.replace(/^\d+_?/, '');
  if (fileName.length <= maxLength) return fileName;
  const truncateLength = Math.max(maxLength, minLength);
  const dot = fileName.lastIndexOf('.');
  const extension = dot >= 0 ? fileName.slice(dot) : '';
  return `${fileName.slice(0, truncateLength)}..${extension}`;
}

/**
 * Mirrors Flutter GenericHelpers.formatDateFromEpoch: '' for 0, otherwise
 * DateFormat('dd MMM yyyy, hh:mm a', appLanguage).
 */
function formatDateFromEpoch(epoch: number, locale: string): string {
  if (!epoch) return '';
  const date = new Date(epoch);
  if (isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const localized = (loc: string) => ({
    month: new Intl.DateTimeFormat(loc, { month: 'short' }).format(date),
    period:
      new Intl.DateTimeFormat(loc, { hour: 'numeric', hour12: true })
        .formatToParts(date)
        .find((part) => part.type === 'dayPeriod')?.value ?? '',
  });
  let parts: { month: string; period: string };
  try {
    parts = localized(locale);
  } catch {
    parts = localized('en');
  }
  const hours = date.getHours() % 12 || 12;
  return `${pad(date.getDate())} ${parts.month} ${date.getFullYear()}, ${pad(hours)}:${pad(date.getMinutes())} ${parts.period}`;
}

/* ── Icons ── */

type IconProps = { className?: string };

const strokeProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

function UserIcon({ className }: IconProps) {
  return (
    <svg className={className} viewBox="0 0 43 53" fill="currentColor" aria-hidden="true">
      <path d="M0.21 40.87c-.2.6-.09 1.26.3 1.75C5.22 48.54 12.81 52.41 21.37 52.41s16.15-3.87 20.86-9.79c.39-.49.5-1.15.3-1.75C39.69 32.57 31.3 26.5 21.37 26.5S3.06 32.57.21 40.87Z" />
      <path d="M21.37 22.8c6.13 0 11.1-4.97 11.1-11.1S27.5.6 21.37.6 10.27 5.57 10.27 11.7s4.97 11.1 11.1 11.1Z" />
    </svg>
  );
}

function UserSmallIcon() {
  return (
    <svg width="18" height="18" {...strokeProps}>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 20.5c.9-3.7 4-5.5 7.5-5.5s6.6 1.8 7.5 5.5" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg width="18" height="18" {...strokeProps}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}

function PhoneIcon() {
  return (
    <svg width="18" height="18" {...strokeProps}>
      <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2Z" />
    </svg>
  );
}

function LinkIcon() {
  return (
    <svg width="18" height="18" {...strokeProps}>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3A4 4 0 0 0 11 18.7l1-1" />
    </svg>
  );
}

function IdIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="11" r="2" />
      <path d="M6 16c.5-1.5 1.7-2 3-2s2.5.5 3 2M15 10h3M15 14h3" />
    </svg>
  );
}

function PlusIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps} strokeWidth={2.2}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}

function PencilIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps}>
      <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
      <path d="m13.5 6.5 4 4" />
    </svg>
  );
}

function BriefcaseIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" />
    </svg>
  );
}

function LocationIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps}>
      <path d="M12 21s-7-6.2-7-11.5a7 7 0 0 1 14 0C19 14.8 12 21 12 21Z" />
      <circle cx="12" cy="9.5" r="2.5" />
    </svg>
  );
}

function FileIcon() {
  return (
    <svg width="20" height="20" {...strokeProps}>
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
      <path d="M14 3v5h5M9 13h6M9 17h4" />
    </svg>
  );
}

function UploadIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps} strokeWidth={2}>
      <path d="M12 16V4M7 9l5-5 5 5M5 20h14" />
    </svg>
  );
}

function DownloadIcon({ className }: IconProps) {
  return (
    <svg className={className} {...strokeProps} strokeWidth={2}>
      <path d="M12 4v12M7 11l5 5 5-5M5 20h14" />
    </svg>
  );
}
